import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, Banknote, Zap, TrendingUp, Users, CheckCircle, AlertTriangle, Activity, Calendar as CalendarIcon, ChevronRight, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const CHART_TOOLTIP = { contentStyle:{ background:'var(--nd-surface)', border:'1px solid var(--nd-w10)', borderRadius:'10px', boxShadow:'0 8px 24px rgba(0,0,0,0.40)', fontSize:'12px', color:'var(--nd-text)' }, cursor:{ fill:'var(--nd-w04)' } };
const TICK = { fill:'var(--nd-tx30)', fontSize:11 };
const GRID = { strokeDasharray:'3 3', vertical:false, stroke:'var(--nd-w05)' };
const PIE_COLORS = ['#3B82F6', '#22D3A5'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function StatCard({ title, value, icon:Icon, color, trend }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} style={{ background:'var(--nd-surface)', border:`1px solid ${hov ? `${color}30` : 'var(--nd-w06)'}`, borderRadius:'12px', padding:'18px 20px', transition:'border-color .2s, transform .2s', transform:hov?'translateY(-3px)':'none' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
        <div>
          <div style={{ fontSize:'10px', fontWeight:'700', letterSpacing:'0.10em', textTransform:'uppercase', color:'var(--nd-tx30)', marginBottom:'8px' }}>{title}</div>
          <div style={{ fontSize:'26px', fontWeight:'800', color:'var(--nd-text)', fontFamily:"'DM Mono',monospace", letterSpacing:'-0.02em', lineHeight:1 }}>{value}</div>
        </div>
        <div style={{ width:'44px', height:'44px', borderRadius:'10px', background:`${color}12`, border:`1px solid ${color}20`, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Icon size={20} color={color}/>
        </div>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
        <TrendingUp size={12} color={color}/>
        <span style={{ fontSize:'11px', fontWeight:'700', color: color }}>{trend}</span>
      </div>
    </div>
  );
}

const CommercialDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()+1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [businessStats, setBusinessStats] = useState({ totalRevenue:0, totalCapacityMW:0, totalClients:0, commercialCount:0, industrialCount:0 });
  const [operationStats, setOperationStats] = useState({ scheduledCount:0, completedMonth:0, pendingMonth:0, completionRate:0, missedJobs:0 });
  const [teamStats, setTeamStats] = useState({ totalTeams:0, totalDailyCapacity:0, activeTeams:0 });
  const [monthlyJobData, setMonthlyJobData] = useState([]);
  
  // Pending & Due state
  const [pendingOrders, setPendingOrders] = useState({ ci: [], residential: [] });
  const [dueOrders, setDueOrders] = useState({ ci: [], residential: [] });

  useEffect(() => { fetchDashboardData(); }, [selectedMonth, selectedYear]);

  const fetchDashboardData = async () => {
    setLoading(true); setError(null);
    try {
      const [clientsRes, teamsRes, calendarRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-clients`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/teams?active_only=true`),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/schedule/calendar/${selectedMonth}/${selectedYear}`)
      ]);
      const clients = clientsRes.data.data?.clients || [];
      setBusinessStats({ totalRevenue: clients.reduce((s,c)=>s+(parseFloat(c.sales_price_before_tax)||0),0), totalCapacityMW: (clients.reduce((s,c)=>s+(parseFloat(c.total_system_size)||0),0)/1000).toFixed(2), totalClients: clients.length, commercialCount: clients.filter(c=>c.contract_type==='commercial').length, industrialCount: clients.filter(c=>c.contract_type==='industrial').length });
      const teams = teamsRes.data.teams || [];
      setTeamStats({ totalTeams:teams.length, totalDailyCapacity:teams.reduce((s,t)=>s+(parseInt(t.max_daily_capacity)||0),0), activeTeams:teams.length });
      const calClients = calendarRes.data.clients || [];
      let scheduled=0, completed=0, missed=0;
      const jpd = {}; const days = new Date(selectedYear,selectedMonth,0).getDate();
      for(let i=1;i<=days;i++) jpd[i]=0;
      calClients.forEach(cl => { (cl.schedules||[]).forEach(j => { const d=j.scheduled_date?.split('T')[0]; scheduled++; if(j.status==='completed') completed++; const jd=new Date(d); const today=new Date(); today.setHours(0,0,0,0); if(j.status==='missed'||(j.status!=='completed'&&jd<today&&selectedMonth<=(today.getMonth()+1))) missed++; if(d) { const day=new Date(d).getDate(); if(jpd[day]!==undefined) jpd[day]++; } }); });
      setOperationStats({ scheduledCount:scheduled, completedMonth:completed, pendingMonth:scheduled-completed, completionRate:scheduled>0?Math.round(completed/scheduled*100):0, missedJobs:missed });
      setMonthlyJobData(Object.keys(jpd).map(d=>({ name:d, Jobs:jpd[d] })));

      // Fetch pending and due jobs
      const todayStr = new Date().toISOString().split('T')[0];
      const startPrev = new Date(selectedYear, selectedMonth - 2, 1).toISOString().split('T')[0];
      
      const [pendingRes, dueRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/job-orders/ops-job-orders`, { params: { specificDate: todayStr, limit: 200 } }),
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/job-orders/ops-job-orders`, { params: { startDate: startPrev, endDate: todayStr, status: 'Pending', limit: 200 } })
      ]);
      
      const categorizeJobs = (jobsArr) => {
        const ci = []; const residential = [];
        (jobsArr || []).filter(j => !['dispatched', 'completed'].includes(j.status?.toLowerCase())).forEach(j => {
          const typeStr = String(j.client_type || j.contract_type || j.customer_type || '').toLowerCase();
          if (typeStr === '2' || typeStr === 'commercial') ci.push(j);
          else residential.push(j);
        });
        return { ci, residential };
      };
      
      setPendingOrders(categorizeJobs(pendingRes.data?.rows || []));
      setDueOrders(categorizeJobs(dueRes.data?.rows || []));
      
    } catch(e) { setError("Failed to load dashboard data."); }
    finally { setLoading(false); }
  };

  const fmtCurrency = (v) => new Intl.NumberFormat('en-PK',{style:'currency',currency:'PKR',maximumSignificantDigits:3}).format(v);
  const loadPct = teamStats.totalDailyCapacity > 0 ? Math.min(100, Math.round(operationStats.scheduledCount/30/teamStats.totalDailyCapacity*100)) : 0;

  const selectStyle = { padding:'9px 12px', background:'var(--nd-w08)', border:'1px solid var(--nd-w12)', borderRadius:'8px', color:'var(--nd-text)', fontSize:'13px', fontWeight:'600', outline:'none', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", colorScheme:'dark' };

  return (
    <div style={{ padding:'14px 20px', fontFamily:"'DM Sans',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap'); @keyframes ci-spin{to{transform:rotate(360deg)}} select option{background:var(--nd-surface-2);color:var(--nd-text)}`}</style>

      {/* Header */}
      <div style={{ background:'var(--nd-surface)', border:'1px solid var(--nd-w06)', borderRadius:'14px', padding:'20px 24px 18px', marginBottom:'18px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', fontWeight:'700', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--nd-tx25)', marginBottom:'10px' }}>
          <span>Commercial</span><ChevronRight size={12} color="var(--nd-w12)"/><span style={{ color:'rgba(34,211,165,0.55)' }}>CI Dashboard</span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', flexWrap:'wrap', gap:'12px', marginBottom:'14px' }}>
          <div>
            <div style={{ fontSize:'26px', fontWeight:'800', color:'var(--nd-tx85)', letterSpacing:'-0.03em', display:'flex', alignItems:'center', gap:'12px' }}><Building2 size={22} color="#3B82F6"/>CI Admin Dashboard</div>
            <div style={{ fontSize:'12px', color:'var(--nd-tx30)', marginTop:'5px' }}>Performance Overview & Historical Data</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', background:'var(--nd-w04)', border:'1px solid var(--nd-w08)', borderRadius:'10px' }}>
            <CalendarIcon size={15} color="var(--nd-tx35)"/>
            <select value={selectedMonth} onChange={e=>setSelectedMonth(parseInt(e.target.value))} style={selectStyle}>
              {MONTH_NAMES.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
            </select>
            <select value={selectedYear} onChange={e=>setSelectedYear(parseInt(e.target.value))} style={selectStyle}>
              {[2024,2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={fetchDashboardData} style={{ width:'34px', height:'34px', background:'linear-gradient(90deg,#3B82F6,#22D3A5)', border:'none', borderRadius:'8px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <RefreshCw size={15} color="white"/>
            </button>
          </div>
        </div>
        <div style={{ height:'2px', borderRadius:'2px', background:'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }}/>
      </div>

      {loading ? (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'50vh' }}>
          <div style={{ textAlign:'center' }}>
            <div style={{ width:'40px', height:'40px', border:'3px solid var(--nd-w08)', borderTop:'3px solid #3B82F6', borderRadius:'50%', animation:'ci-spin 1s linear infinite', margin:'0 auto' }}/>
            <p style={{ color:'var(--nd-tx30)', fontSize:'13px', marginTop:'14px' }}>Loading data for {MONTH_NAMES[selectedMonth-1]}…</p>
          </div>
        </div>
      ) : (<>
        {error && <div style={{ background:'rgba(248,113,113,0.07)', border:'1px solid rgba(248,113,113,0.18)', borderRadius:'10px', padding:'13px 16px', color:'#F87171', fontSize:'13px', fontWeight:'600', marginBottom:'16px' }}>⚠ {error}</div>}

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'14px', marginBottom:'18px' }}>
          <StatCard title="Total Contract Value" value={fmtCurrency(businessStats.totalRevenue)} icon={Banknote} color="#34D399" trend="Current Portfolio"/>
          <StatCard title="Managed Capacity" value={`${businessStats.totalCapacityMW} MW`} icon={Zap} color="#FBBF24" trend={`${businessStats.totalClients} Clients`}/>
          <StatCard title={`${MONTH_NAMES[selectedMonth-1]} Completion`} value={`${operationStats.completionRate}%`} icon={CheckCircle} color="#3B82F6" trend={`${operationStats.completedMonth} / ${operationStats.scheduledCount} Jobs`}/>
          <StatCard title="Missed / Overdue" value={operationStats.missedJobs} icon={AlertTriangle} color="#F87171" trend="Action Required"/>
        </div>

        {/* Charts row */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:'14px', marginBottom:'18px' }}>
          {/* Bar chart */}
          <div style={{ background:'var(--nd-surface)', border:'1px solid var(--nd-w06)', borderRadius:'14px', padding:'20px' }}>
            <div style={{ fontSize:'14px', fontWeight:'800', color:'var(--nd-text)', marginBottom:'3px' }}>Job Execution Flow</div>
            <div style={{ fontSize:'11px', color:'var(--nd-tx30)', marginBottom:'18px' }}>Daily breakdown for {MONTH_NAMES[selectedMonth-1]} {selectedYear}</div>
            <div style={{ height:'260px' }}>
              <ResponsiveContainer>
                <BarChart data={monthlyJobData}>
                  <CartesianGrid {...GRID}/>
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={TICK} interval={2}/>
                  <YAxis axisLine={false} tickLine={false} tick={TICK}/>
                  <Tooltip {...CHART_TOOLTIP}/>
                  <defs><linearGradient id="ciBarGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={1}/><stop offset="100%" stopColor="#22D3A5" stopOpacity={0.8}/></linearGradient></defs>
                  <Bar dataKey="Jobs" fill="url(#ciBarGrad)" radius={[4,4,0,0]} barSize={16}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Right column */}
          <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
            {/* Pie */}
            <div style={{ background:'var(--nd-surface)', border:'1px solid var(--nd-w06)', borderRadius:'14px', padding:'18px 20px' }}>
              <div style={{ fontSize:'10px', fontWeight:'700', color:'var(--nd-tx30)', letterSpacing:'0.10em', textTransform:'uppercase', marginBottom:'12px' }}>Client Portfolio Distribution</div>
              <div style={{ height:'160px', position:'relative' }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={[{ name:'Commercial', value:businessStats.commercialCount },{ name:'Industrial', value:businessStats.industrialCount }]} cx="50%" cy="50%" innerRadius={45} outerRadius={65} paddingAngle={4} dataKey="value">
                      {PIE_COLORS.map((c,i)=><Cell key={i} fill={c}/>)}
                    </Pie>
                    <Tooltip {...CHART_TOOLTIP}/>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', textAlign:'center' }}>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'22px', fontWeight:'800', color:'var(--nd-text)' }}>{businessStats.totalClients}</div>
                  <div style={{ fontSize:'9px', color:'var(--nd-tx30)', fontWeight:'700', letterSpacing:'0.12em', textTransform:'uppercase' }}>CLIENTS</div>
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'center', gap:'16px', marginTop:'10px' }}>
                {[{ label:`Commercial (${businessStats.commercialCount})`, c:PIE_COLORS[0] },{ label:`Industrial (${businessStats.industrialCount})`, c:PIE_COLORS[1] }].map(({ label, c }) => (
                  <div key={label} style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color:'var(--nd-tx45)', fontWeight:'600' }}>
                    <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:c }}/>
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {/* Capacity meter */}
            <div style={{ background:'var(--nd-surface)', border:'1px solid var(--nd-w06)', borderRadius:'14px', padding:'18px 20px', flex:1 }}>
              <div style={{ fontSize:'10px', fontWeight:'700', color:'var(--nd-tx30)', letterSpacing:'0.10em', textTransform:'uppercase', marginBottom:'14px' }}>Operational Capacity (Daily)</div>
              <div style={{ marginBottom:'16px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'8px' }}>
                  <span style={{ fontSize:'13px', fontWeight:'700', color:'var(--nd-text)' }}>Average Daily Load</span>
                  <span style={{ fontFamily:"'DM Mono',monospace", fontSize:'12px', color:'var(--nd-tx35)' }}>{Math.round(operationStats.scheduledCount/30)} / {teamStats.totalDailyCapacity}</span>
                </div>
                <div style={{ height:'7px', borderRadius:'4px', background:'var(--nd-w08)', overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${loadPct}%`, borderRadius:'4px', background:'linear-gradient(90deg,#3B82F6,#22D3A5)', transition:'width .5s' }}/>
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 14px', background:'var(--nd-w03)', border:'1px solid var(--nd-w06)', borderRadius:'10px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <div style={{ width:'34px', height:'34px', borderRadius:'9px', background:'rgba(59,130,246,0.12)', border:'1px solid rgba(59,130,246,0.20)', display:'flex', alignItems:'center', justifyContent:'center' }}><Users size={16} color="#3B82F6"/></div>
                  <div>
                    <div style={{ fontSize:'13px', fontWeight:'700', color:'var(--nd-text)' }}>Active Teams</div>
                    <div style={{ fontSize:'11px', color:'var(--nd-tx30)' }}>Field Force</div>
                  </div>
                </div>
                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:'24px', fontWeight:'800', color:'var(--nd-text)' }}>{teamStats.activeTeams}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Pending & Due Orders Section */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px', marginBottom: '18px' }}>
          {/* C&I Operations */}
          <div style={{ background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: '14px', padding: '18px 20px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '14px', fontWeight: '800', color: 'var(--nd-text)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}><Building2 size={16} color="#3B82F6"/> C&I Operations</div>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--nd-tx30)', letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: '8px' }}>Pending Today ({pendingOrders.ci.length})</div>
              {pendingOrders.ci.length > 0 ? (
                <div style={{ maxHeight: '140px', overflowY: 'auto' }}>
                  {pendingOrders.ci.map(job => (
                    <div key={job.job_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', borderBottom: '1px solid var(--nd-w04)', fontSize: '12px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--nd-text)' }}>{job.customer_name}</span>
                      <span style={{ color: 'var(--nd-tx45)' }}>{job.city}</span>
                    </div>
                  ))}
                </div>
              ) : <div style={{ fontSize: '12px', color: 'var(--nd-tx30)' }}>No pending orders</div>}
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--nd-tx30)', letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: '8px' }}>Overdue / Due ({dueOrders.ci.length})</div>
              {dueOrders.ci.length > 0 ? (
                <div style={{ maxHeight: '140px', overflowY: 'auto' }}>
                  {dueOrders.ci.map(job => (
                    <div key={job.job_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', borderBottom: '1px solid var(--nd-w04)', fontSize: '12px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--nd-text)' }}>{job.customer_name}</span>
                      <span style={{ color: '#F87171' }}>{new Date(job.scheduled_date).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              ) : <div style={{ fontSize: '12px', color: 'var(--nd-tx30)' }}>No overdue orders</div>}
            </div>
          </div>
        </div>
      </>)}
    </div>
  );
};

export default CommercialDashboard;